import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { GraduationCap, User, BookOpen, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import { getMyChildren, type ParentChild } from '../../modules/roster/api/roster.client';

export const ChildrenList: React.FC = () => {
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMyChildren();
        if (!cancelled) setChildren(data);
      } catch (e: unknown) {
        if (!cancelled) {
          const message = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to load children';
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <PageHeader
        title="My Children"
        subtitle="View your linked children's details"
        actions={
          <Button onClick={() => navigate(ROUTES.PARENT_LINK_CHILD)} className="w-full sm:w-auto">
            Link Another Child
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <CardContent padding="lg" className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
          </CardContent>
        </Card>
      ) : children.length === 0 ? (
        <Card>
          <CardContent padding="lg" className="text-center">
            <GraduationCap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Children Linked</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Link your account to your child's student record to view their details.
            </p>
            <Button onClick={() => navigate(ROUTES.PARENT_LINK_CHILD)}>
              Link Child
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {children.map((child) => (
            <Card key={child.id} hover className="min-w-0 overflow-hidden">
              <CardContent className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <User className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold break-words">
                        {child.display_name}
                      </h3>
                      {(child.first_name || child.last_name) && (
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                          {[child.first_name, child.last_name].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant={child.status === 'active' ? 'success' : 'secondary'} className="shrink-0">
                    {child.status}
                  </Badge>
                </div>
                <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {child.admission_no && (
                    <div className="flex items-center gap-2 text-sm">
                      <Hash className="h-4 w-4 text-gray-500 shrink-0" />
                      <span className="text-gray-600 dark:text-gray-400 shrink-0">Admission No.</span>
                      <span className="font-medium break-words">{child.admission_no}</span>
                    </div>
                  )}
                  {child.class && (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <BookOpen className="h-4 w-4 text-gray-500 shrink-0" />
                      <span className="text-gray-600 dark:text-gray-400 shrink-0">Class</span>
                      <span className="font-medium break-words">{child.class.name}</span>
                      {child.class.grade && (
                        <span className="text-gray-500">(Grade {child.class.grade})</span>
                      )}
                    </div>
                  )}
                  {child.section && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 dark:text-gray-400 shrink-0">Section</span>
                      <span className="font-medium break-words">{child.section.name}</span>
                    </div>
                  )}
                  {child.section?.academic_year_key && (
                    <div className="text-sm text-gray-500">
                      Academic year: {child.section.academic_year_key}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
};

